import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import {
  getSubscriptionStatus,
  createCheckoutSession,
  createPortalSession,
  cancelSubscription,
  reactivateSubscription,
  changePlan,
  cancelTrial,
  resumeTrial,
} from '../services/subscriptionService';
import { ApiError } from '../services/ApiError';
import { useAuth } from './useAuth';

export const useSubscription = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Query for subscription status
  const {
    data: subscriptionData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['subscription', user?.uid],
    queryFn: async () => {
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();
      return getSubscriptionStatus(token);
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const subscription = subscriptionData?.subscription;

  // Helper to check if user has access
  const hasAccess = () => {
    if (!subscription) return false;
    
    if (subscription.status === 'active') return true;
    
    if (subscription.status === 'trial' && subscription.trialEndsAt) {
      return new Date(subscription.trialEndsAt) > new Date();
    }
    
    return false;
  };

  // Helper to get days remaining in trial
  const getTrialDaysRemaining = () => {
    if (subscription?.status !== 'trial' || !subscription.trialEndsAt) return 0;
    
    const trialEnd = new Date(subscription.trialEndsAt);
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  };

  // Helper to check if the user asked to cancel their trial. Doesn't affect
  // access — the trial still runs to trialEndsAt — only what we show and send.
  const isTrialCanceled = () =>
    subscription?.status === 'trial' && !!subscription.trialCanceledAt;

  // Helper to check if trial is expiring soon (within 3 days)
  const isTrialExpiringSoon = () => {
    const daysRemaining = getTrialDaysRemaining();
    return subscription?.status === 'trial' && daysRemaining <= 3 && daysRemaining > 0;
  };

  // Create checkout session mutation
  const createCheckoutMutation = useMutation({
    mutationFn: async (planType: 'monthly' | 'annual') => {
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();
      return createCheckoutSession({ planType }, token);
    },
    onSuccess: (data) => {
      // Redirect to Stripe checkout
      window.location.href = data.url;
    },
    onError: (error: Error) => {
      if (error instanceof ApiError && error.code === 'SUBSCRIPTION_EXISTS') {
        refetch();
        notifications.show({
          title: 'Already subscribed',
          message: 'Your subscription is already active!',
          color: 'green',
        });
        return;
      }
      // The subscription exists but is behind on payment. Buying a second one
      // isn't the fix, and Stripe wouldn't allow it anyway. Refetching surfaces
      // the past-due banner, which points at the billing portal.
      if (
        error instanceof ApiError &&
        error.code === 'SUBSCRIPTION_NEEDS_PAYMENT_UPDATE'
      ) {
        refetch();
        notifications.show({
          title: 'Payment method needs updating',
          message: 'Use Update Payment Method to restore your subscription.',
          color: 'orange',
        });
        return;
      }
      notifications.show({
        title: 'Checkout error',
        message: error.message || 'Failed to create checkout session',
        color: 'red',
      });
    },
  });

  // Stripe-hosted billing portal, for replacing the card on file and settling an
  // overdue invoice. Full-page redirect, same as checkout.
  const openBillingPortalMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();
      return createPortalSession(token);
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Billing error',
        message: error.message || 'Failed to open the billing portal',
        color: 'red',
      });
    },
  });

  // Cancel subscription mutation
  const cancelMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();
      return cancelSubscription(token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      notifications.show({
        title: 'Subscription canceled',
        message: 'Your subscription will be canceled at the end of the current period',
        color: 'yellow',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Cancellation error',
        message: error.message || 'Failed to cancel subscription',
        color: 'red',
      });
    },
  });

  // Reactivate subscription mutation
  const reactivateMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();
      return reactivateSubscription(token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      notifications.show({
        title: 'Subscription reactivated',
        message: 'Your subscription has been reactivated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Reactivation error',
        message: error.message || 'Failed to reactivate subscription',
        color: 'red',
      });
    },
  });

  // Cancel trial mutation. Nothing is billed during a trial, so the copy here
  // leads with that rather than with the cancellation.
  const cancelTrialMutation = useMutation({
    mutationFn: async (reason?: string) => {
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();
      return cancelTrial(token, reason);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      notifications.show({
        title: 'Trial canceled',
        message: "You won't be charged. We never had a card on file, and you keep access until your trial ends.",
        color: 'blue',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Cancellation error',
        message: error.message || 'Failed to cancel trial',
        color: 'red',
      });
    },
  });

  // Resume trial mutation (undo a trial cancellation)
  const resumeTrialMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();
      return resumeTrial(token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      notifications.show({
        title: 'Trial resumed',
        message: 'Your trial is back on. Enjoy the rest of it.',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Something went wrong',
        message: error.message || 'Failed to resume trial',
        color: 'red',
      });
    },
  });

  // Change plan mutation
  const changePlanMutation = useMutation({
    mutationFn: async (planType: 'monthly' | 'annual') => {
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();
      return changePlan(planType, token);
    },
    onSuccess: (_, planType) => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      notifications.show({
        title: 'Plan changed',
        message: `Successfully changed to ${planType} plan`,
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Plan change error',
        message: error.message || 'Failed to change plan',
        color: 'red',
      });
    },
  });

  return {
    subscription,
    isLoading,
    error,
    refetch,
    hasAccess: hasAccess(),
    getTrialDaysRemaining,
    isTrialExpiringSoon: isTrialExpiringSoon(),
    isTrialCanceled: isTrialCanceled(),
    // Actions
    createCheckout: createCheckoutMutation.mutate,
    openBillingPortal: openBillingPortalMutation.mutate,
    cancelSubscription: cancelMutation.mutate,
    reactivateSubscription: reactivateMutation.mutate,
    changePlan: changePlanMutation.mutate,
    cancelTrial: cancelTrialMutation.mutate,
    resumeTrial: resumeTrialMutation.mutate,
    // Loading states
    isCreatingCheckout: createCheckoutMutation.isPending,
    isOpeningBillingPortal: openBillingPortalMutation.isPending,
    isCanceling: cancelMutation.isPending,
    isReactivating: reactivateMutation.isPending,
    isChangingPlan: changePlanMutation.isPending,
    isCancelingTrial: cancelTrialMutation.isPending,
    isResumingTrial: resumeTrialMutation.isPending,
  };
};